import { createContext, useContext, useEffect, useState, ReactNode, useCallback } from 'react';
import { useAuth0 } from '../auth/useAuth0';
import { getMyPermissions, PermissionGrant } from '../service/permission/permission-service';
import { useBusinessContext } from './BusinessContext';

interface PermissionsContextType {
    /** Effective grants keyed by permission key; null while loading. */
    grants: Record<string, PermissionGrant> | null;
    loading: boolean;
    /** Can the caller view this permission area? Defaults to TRUE while still
     *  loading (avoids a flash of an empty menu — owners/admins see all anyway). */
    canView: (permissionKey: string) => boolean;
    /** Does the caller have business-wide (all) scope for this permission? */
    allScope: (permissionKey: string) => boolean;
    /** True when the caller is a business admin/owner (full implicit access). */
    isAdmin: boolean;
    reload: () => void;
}

const PermissionsContext = createContext<PermissionsContextType | undefined>(undefined);

export const PermissionsProvider = ({ children }: { children: ReactNode }) => {
    const { getAccessTokenSilently, isAuthenticated } = useAuth0();
    const { selectedBusiness } = useBusinessContext();
    const businessId = selectedBusiness?.id;

    const [grants, setGrants] = useState<Record<string, PermissionGrant> | null>(null);
    const [loading, setLoading] = useState<boolean>(false);

    const load = useCallback(async () => {
        if (!isAuthenticated || !businessId) {
            setGrants(null);
            return;
        }
        setLoading(true);
        try {
            const token = await getAccessTokenSilently();
            const list = await getMyPermissions(businessId, token);
            const map: Record<string, PermissionGrant> = {};
            for (const g of list) if (g.permissionKey) map[g.permissionKey] = g;
            setGrants(map);
        } catch {
            // On failure, leave grants null → menu shows everything (fail-open for
            // the UI only; the backend still enforces every operation).
            setGrants(null);
        } finally {
            setLoading(false);
        }
    }, [isAuthenticated, businessId, getAccessTokenSilently]);

    useEffect(() => {
        load();
    }, [load]);

    const canView = useCallback(
        (permissionKey: string) => (grants ? grants[permissionKey]?.canView === true : true),
        [grants]
    );
    const allScope = useCallback(
        (permissionKey: string) => (grants ? grants[permissionKey]?.allScope === true : true),
        [grants]
    );

    // Admins resolve to all-scope on every permission with a null memberId
    // (the backend short-circuit) — that's the reliable signal.
    const isAdmin = (() => {
        if (!grants) return false;
        const vals = Object.values(grants);
        return vals.length > 0 && vals.every((g) => g.allScope) && vals[0].memberId == null;
    })();

    return (
        <PermissionsContext.Provider value={{ grants, loading, canView, allScope, isAdmin, reload: load }}>
            {children}
        </PermissionsContext.Provider>
    );
};

export const usePermissions = () => {
    const ctx = useContext(PermissionsContext);
    if (ctx === undefined) {
        throw new Error('usePermissions must be used within a PermissionsProvider');
    }
    return ctx;
};
